import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, ExternalLink, Crosshair } from 'lucide-react';

// Tracks whether the Google Maps JS API failed to authenticate. Google invokes
// window.gm_authFailure() when the API key is rejected (billing off, API not
// enabled, referrer not allowed, etc.). We flip a shared flag so every map
// surface can render a tasteful placeholder instead of Google's grey "Oops" box.
let _mapsUnavailable = false;
const _listeners = new Set();

const markUnavailable = () => {
  if (_mapsUnavailable) return;
  _mapsUnavailable = true;
  _listeners.forEach((fn) => { try { fn(true); } catch (e) { /* noop */ } });
};

if (typeof window !== 'undefined') {
  const prev = window.gm_authFailure;
  window.gm_authFailure = () => {
    markUnavailable();
    if (typeof prev === 'function') { try { prev(); } catch (e) { /* noop */ } }
  };
}

export const useMapsUnavailable = () => {
  const [unavailable, setUnavailable] = useState(_mapsUnavailable);
  useEffect(() => {
    const fn = (v) => setUnavailable(v);
    _listeners.add(fn);
    setUnavailable(_mapsUnavailable);
    return () => { _listeners.delete(fn); };
  }, []);
  return unavailable;
};

export const MapUnavailable = ({ center, label, className = '', style, setGpsHref }) => {
  const navigate = useNavigate();
  const lat = center?.lat != null ? Number(center.lat) : null;
  const lng = center?.lng != null ? Number(center.lng) : null;
  const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);
  const href = hasCoords
    ? `https://www.google.com/maps?q=${lat},${lng}`
    : 'https://www.google.com/maps';
  const embeddedPreviewHref = hasCoords
    ? `https://www.google.com/maps?q=${encodeURIComponent(`${lat},${lng}`)}&z=16&output=embed`
    : null;

  if (hasCoords) {
    return (
      <div
        data-testid="map-unavailable"
        className={`relative h-full w-full overflow-hidden bg-slate-900 ${className}`}
        style={{ minHeight: '100%', ...style }}
      >
        <iframe
          title={`Map preview at ${lat.toFixed(5)}, ${lng.toFixed(5)}`}
          src={embeddedPreviewHref}
          className="h-full w-full border-0"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          data-testid="map-unavailable-embedded-preview"
        />
        <div className="pointer-events-none absolute inset-x-0 top-0 flex items-start justify-between gap-3 bg-gradient-to-b from-slate-950/85 to-transparent p-3 text-left">
          <div className="min-w-0 rounded-lg bg-slate-950/75 px-3 py-2 text-white shadow-lg backdrop-blur-sm">
            <p className="text-xs font-semibold">Location map preview</p>
            {label && <p className="mt-0.5 truncate text-[10px] text-slate-300">{label}</p>}
            <p className="mt-1 font-mono text-[10px] text-slate-200">
              {lat.toFixed(5)}, {lng.toFixed(5)}
            </p>
          </div>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="map-unavailable-open-link"
            className="pointer-events-auto inline-flex shrink-0 items-center gap-1.5 rounded-full bg-slate-950/75 px-3 py-1.5 text-[11px] font-medium text-white shadow-lg backdrop-blur-sm transition-colors hover:bg-slate-900"
          >
            <ExternalLink className="h-3 w-3" /> Open in Google Maps
          </a>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="map-unavailable"
      className={`relative w-full h-full overflow-hidden flex flex-col items-center justify-center text-center ${className}`}
      style={{ background: 'linear-gradient(135deg,#0f172a 0%,#1e293b 55%,#334155 100%)', minHeight: '100%', ...style }}
    >
      <div
        className="absolute inset-0 opacity-[0.10] pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(#94a3b8 1px,transparent 1px),linear-gradient(90deg,#94a3b8 1px,transparent 1px)',
          backgroundSize: '24px 24px',
        }}
      />
      <div className="relative z-10 px-4 py-3">
        <div className="mx-auto mb-2.5 flex h-11 w-11 items-center justify-center rounded-full bg-sky-500/20 ring-1 ring-sky-400/40">
          <MapPin className="h-5 w-5 text-sky-300" />
        </div>
        <p className="text-slate-100 text-sm font-semibold tracking-tight">Map preview unavailable</p>
        {label && <p className="text-slate-400 text-[11px] mt-0.5">{label}</p>}
        {hasCoords && (
          <p className="text-slate-300 text-[11px] mt-1.5 font-mono">{lat.toFixed(5)}, {lng.toFixed(5)}</p>
        )}
        {!hasCoords && (
          <p className="text-amber-300/90 text-[11px] mt-1.5">No GPS set for this location</p>
        )}
        <div className="mt-3 flex items-center justify-center gap-2 flex-wrap">
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="map-unavailable-open-link"
            className="inline-flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 px-3.5 py-1.5 text-[11px] font-medium text-white transition-colors"
          >
            <ExternalLink className="h-3 w-3" /> Open in Google Maps
          </a>
          {setGpsHref && !hasCoords && (
            <button
              type="button"
              onClick={() => navigate(setGpsHref)}
              data-testid="map-unavailable-set-gps"
              className="inline-flex items-center gap-1.5 rounded-full bg-sky-500/30 px-3.5 py-1.5 text-[11px] font-medium text-white ring-1 ring-sky-400/40 transition-colors hover:bg-sky-500/50"
            >
              <Crosshair className="h-3 w-3" /> Set GPS
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MapUnavailable;
